import React, { useState, useRef, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";

export default function TripChat({ trip }) {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([
    { role: "assistant", content: `Hi! I know your ${trip.destination} trip inside out. Ask me anything — "what if I skip day 2?", "restaurants near Day 1?", "how tight is my budget?", etc.` }
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [msgs, open]);

  const send = async (e) => {
    e?.preventDefault();
    const message = input.trim();
    if (!message || busy) return;
    setInput("");
    const history = msgs.map(m => ({ role: m.role, content: m.content }));
    setMsgs(m => [...m, { role: "user", content: message }, { role: "assistant", content: "…", loading: true }]);
    setBusy(true);
    try {
      const { data } = await api.post(`/trips/${trip.trip_id}/chat`, { message, history });
      setMsgs(m => [...m.filter(x => !x.loading), { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMsgs(m => m.filter(x => !x.loading));
      toast.error(e?.response?.data?.detail || "Chat failed");
    } finally { setBusy(false); }
  };

  return (
    <>
      {/* Floating trigger */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="chat-open-button"
          className="fixed bottom-6 right-6 z-30 w-14 h-14 rounded-full bg-[#C85A32] hover:bg-[#B04B26] text-white shadow-lg shadow-orange-900/30 flex items-center justify-center transition-transform hover:-translate-y-0.5"
          aria-label="Open trip chat"
        >
          <MessageCircle size={22}/>
        </button>
      )}

      {open && (
        <div className="fixed bottom-6 right-6 z-30 w-[92vw] max-w-md h-[70vh] max-h-[600px] rounded-2xl bg-white border border-stone-200 shadow-2xl flex flex-col overflow-hidden" data-testid="chat-panel">
          <header className="p-4 flex items-center justify-between border-b border-stone-200 bg-stone-950 text-white">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[#C85A32] flex items-center justify-center"><Sparkles size={14}/></div>
              <div>
                <div className="font-serif-display text-base font-semibold">Trip companion</div>
                <div className="font-mono-code text-[10px] uppercase tracking-widest text-amber-300">Gemini · knows your itinerary</div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="text-white/70 hover:text-white" data-testid="chat-close-button"><X size={18}/></button>
          </header>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm ${m.role === "user" ? "bg-stone-900 text-white rounded-br-sm" : m.loading ? "bg-stone-100 text-stone-500 animate-pulse rounded-bl-sm" : "bg-amber-50 border border-amber-100 text-stone-800 rounded-bl-sm"}`}>
                  {m.content}
                </div>
              </div>
            ))}
          </div>
          <form onSubmit={send} className="p-3 border-t border-stone-200 flex items-center gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about your trip…"
              className="flex-1 px-3 py-2 rounded-full bg-stone-100 border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#C85A32]/40"
              data-testid="chat-input"
              disabled={busy}
            />
            <Button type="submit" disabled={busy || !input.trim()} className="rounded-full h-10 w-10 p-0 bg-[#C85A32] hover:bg-[#B04B26]" data-testid="chat-send-button">
              <Send size={16}/>
            </Button>
          </form>
        </div>
      )}
    </>
  );
}
